import { ApiResponse, PaginatedResponse } from '@/types'

const API_BASE = '/api'

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retries = 2
  ): Promise<ApiResponse<T>> {
    const token = localStorage.getItem('auth_token')

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    }

    // 添加超时控制（优化为12秒，平衡速度和成功率）
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 12000) // 12秒超时

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...config,
        signal: controller.signal
      })
      clearTimeout(timeoutId)

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || `Request failed with status ${response.status}`)
      }

      return data
    } catch (error) {
      clearTimeout(timeoutId)

      // 如果是网络错误且还有重试次数，则重试
      if (retries > 0 && (error instanceof TypeError || (error instanceof Error && error.name === 'AbortError'))) {
        console.warn(`API request failed, retrying... (${retries} retries left)`)
        await new Promise(resolve => setTimeout(resolve, 1000)) // 等待1秒后重试
        return this.request(endpoint, options, retries - 1)
      }

      console.error('API request failed:', error)
      throw error
    }
  }

  // Auth endpoints
  async login(username: string, password: string) {
    return this.request<{ user: any; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
  }

  async register(username: string, password: string, inviteCode: string) {
    return this.request<{ user: any; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password, inviteCode }),
    })
  }

  async logout() {
    return this.request<{ message: string }>('/auth/logout', {
      method: 'POST',
    })
  }

  async getProfile() {
    return this.request<any>('/auth/profile')
  }

  async updateProfile(data: { username?: string; avatar?: string }) {
    return this.request<any>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Reading records endpoints
  async submitReading(date: string, count: number, bookTitle?: string, notes?: string) {
    return this.request<any>('/readings', {
      method: 'POST',
      body: JSON.stringify({ date, count, bookTitle, notes }),
    })
  }

  async getReadingHistory(page = 1, limit = 20) {
    return this.request<PaginatedResponse<any>>(`/readings?page=${page}&limit=${limit}`)
  }

  async getLeaderboard(period: 'today' | 'week' | 'month' | 'all' = 'all', date?: string) {
    const params = new URLSearchParams({ period })
    if (date && period === 'today') {
      params.append('date', date)
    }
    return this.request<any[]>(`/leaderboard?${params.toString()}`)
  }

  // Admin endpoints
  async createInviteCode() {
    return this.request<any>('/admin/invite-codes', {
      method: 'POST',
    })
  }

  async getInviteCodes() {
    return this.request<any[]>('/admin/invite-codes')
  }

  async getUsers(page = 1, limit = 20) {
    return this.request<PaginatedResponse<any>>(`/admin/users?page=${page}&limit=${limit}`)
  }

  // File upload
  async uploadAvatar(file: File) {
    const formData = new FormData()
    formData.append('avatar', file)

    const token = localStorage.getItem('auth_token')
    const response = await fetch(`${API_BASE}/upload/avatar`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    if (!response.ok) {
      throw new Error('Upload failed')
    }

    const data = await response.json()
    return data.url
  }
}

export const api = new ApiClient()
